import React, { useState, useRef, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../api/api";
import Swal from "sweetalert2";

function AddVehicle({ setLoading, loading }) {
  const navigate = useNavigate();

  const [step, setStep] = useState(1); // For multi-step form
  const [formData, setFormData] = useState({
    name: "",
    type: "",
    district: "",
    city: "",
    availableSeats: "",
    maxLoadCapacity: "",
    cargoSize: "",
    pricePerKm: "",
    description: "",
    imageURLs: [],
    subscriptionPlan: "Monthly",
  });

  // Feature 3: Lorries & Pick-up trucks capture load capacity instead of seats
  const isLoadVehicle =
    formData.type === "Lorry" || formData.type === "Pickup";


  const [userId, setUserId] = useState([]);
  useEffect(() => {

    const getUserId = async () => {
      setLoading(true);
      api.get("/users/checklogin", { withCredentials: true })
        .then((response) => {
          setUserId(response.data.user.user._id);
        })
        .catch(() => console.log("Not logged in"));
      setLoading(false);
    }
    getUserId();
  }, []);

  // Store actual file objects instead of URLs
  const [imageFiles, setImageFiles] = useState([null, null, null]);
  const [imagePreviews, setImagePreviews] = useState([null, null, null]);
  const [error, setError] = useState("");
  const fileInputRefs = [useRef(), useRef(), useRef()];
  const additionalFileInputs = useRef([]);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  }, []);

  // Numeric input sanitiser: strips anything that isn't a digit (or a single
  // decimal point when allowed), blocks negatives/exponents, and removes leading
  // zeros so a value can never start with 0 (e.g. "01" -> "1", "0" -> "").
  const handleNumericChange = useCallback((e, { decimal = false } = {}) => {
    const { name, value } = e.target;
    let v = value;
    if (decimal) {
      v = v.replace(/[^\d.]/g, "");
      const parts = v.split(".");
      if (parts.length > 2) v = parts[0] + "." + parts.slice(1).join("");
      v = v.replace(/^0+(\d)/, "$1"); // "01.5" -> "1.5", keeps "0.5"
    } else {
      v = v.replace(/[^\d]/g, "").replace(/^0+/, ""); // integers only, no leading zero
    }
    setFormData((prevData) => ({
      ...prevData,
      [name]: v,
    }));
  }, []);

  // Modified to only store the file and create preview, without uploading
  const handleImageChange = (index, e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];

      // Just store the file object for later upload
      const newImageFiles = [...imageFiles];
      newImageFiles[index] = file;
      setImageFiles(newImageFiles);

      // Create image preview
      const reader = new FileReader();
      reader.onloadend = () => {
        const newPreviews = [...imagePreviews];
        newPreviews[index] = reader.result;
        setImagePreviews(newPreviews);
      };
      reader.readAsDataURL(file);
    }
  };

  const addImageSlot = () => {
    setImageFiles([...imageFiles, null]);
    setImagePreviews([...imagePreviews, null]);
    // Create a new ref for the new file input
    const newRef = React.createRef();
    additionalFileInputs.current.push(newRef);
  };

  const removeImageSlot = (index) => {
    if (imageFiles.length > 3) {
      const newImageFiles = [...imageFiles];
      const newImagePreviews = [...imagePreviews];

      newImageFiles.splice(index, 1);
      newImagePreviews.splice(index, 1);

      setImageFiles(newImageFiles);
      setImagePreviews(newImagePreviews);

      // Remove the ref
      if (index >= 3) {
        additionalFileInputs.current.splice(index - 3, 1);
      }
    }
  };

  // New function to handle Cloudinary upload
  const uploadToCloudinary = async (file) => {
    if (!file) return null;

    const data = new FormData();
    data.append('file', file);
    data.append('upload_preset', process.env.REACT_APP_UPLOAD_PRESET);
    data.append('cloud_name', process.env.REACT_APP_CLOUD_NAME);

    setLoading(true);
    const res = await fetch(`https://api.cloudinary.com/v1_1/${process.env.REACT_APP_CLOUD_NAME}/image/upload`, {
      method: 'POST',
      body: data
    });

    const imageUrl = await res.json();
    return imageUrl.url;
    setLoading(false);
  };

  const validateStep = (currentStep) => {
    if (currentStep === 1) {
      if (
        !formData.name ||
        !formData.type ||
        !formData.district ||
        !formData.city
      ) {
        setError("Please fill in all required fields");
        return false;
      }
      if (isLoadVehicle) {
        if (!formData.maxLoadCapacity) {
          setError("Please enter the maximum load capacity");
          return false;
        }
      } else if (!formData.availableSeats) {
        setError("Please fill in all required fields");
        return false;
      }
      return true;
    }
    return true;
  };

  const nextStep = () => {
    if (validateStep(step)) {
      setError("");
      setStep(step + 1);
    }
  };

  const prevStep = () => {
    setStep(step - 1);
  };

  // Modified to upload images at submit time
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate the final step
    const hasAtLeastOneImage = imageFiles.some((file) => file !== null);
    if (!hasAtLeastOneImage) {
      setError("Please upload at least one image");
      return;
    }

    setLoading(true);
    setError("");

    try {
      // Upload all images to Cloudinary
      const uploadPromises = imageFiles
        .filter(file => file !== null)
        .map(file => uploadToCloudinary(file));

      // Wait for all uploads to complete
      const uploadedUrls = await Promise.all(uploadPromises);

      // Filter out any null values (failed uploads)
      const validUrls = uploadedUrls.filter(url => url !== null);

      if (validUrls.length === 0) {
        throw new Error("Failed to upload images. Please try again.");
      }

      // Now send the form data with image URLs
      const vehicleData = {
        ...formData,
        imageURLs: validUrls,
        userId: userId
      };

      setLoading(true);


      api.post("/users/addVehicle", vehicleData)
        .then((response) => {
          const payhereData = response.data.payhereData;

          Swal.fire({
            icon: "info",
            title: "Redirecting to Payment",
            text: "You will be redirected to PayHere to complete your vehicle subscription.",
            timer: 2000,
            showConfirmButton: false
          }).then(() => {
            const form = document.createElement("form");
            form.method = "POST";
            form.action = "https://www.payhere.lk/pay/checkout";

            for (const key in payhereData) {
              if (payhereData.hasOwnProperty(key)) {
                const hiddenField = document.createElement("input");
                hiddenField.type = "hidden";
                hiddenField.name = key;
                hiddenField.value = payhereData[key];
                form.appendChild(hiddenField);
              }
            }

            Object.assign(form.style, {
              display: "none"
            });

            document.body.appendChild(form);
            form.submit();
          });
        })
        .catch((err) => {
          setError(err.response?.data?.message || err.message || "Failed to add vehicle.");
          setLoading(false);
        });
      // Removing navigate("/vehicles"); as we redirect to PayHere now
    } catch (err) {
      setError(
        err.response?.data?.message ||
        err.message ||
        "Failed to add vehicle. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const vehicleTypes = [
    "Car",
    "SUV",
    "Bike",
    "Bus",
    "Lorry",
    "Van",
    "Pickup",
    "Three Wheel",
  ];
  const districts = [
    "Ampara",
    "Anuradhapura",
    "Badulla",
    "Batticaloa",
    "Colombo",
    "Galle",
    "Gampaha",
    "Hambantota",
    "Jaffna",
    "Kalutara",
    "Kandy",
    "Kegalle",
    "Kilinochchi",
    "Kurunegala",
    "Mannar",
    "Matale",
    "Matara",
    "Monaragala",
    "Mullaitivu",
    "Nuwara Eliya",
    "Polonnaruwa",
    "Puttalam",
    "Ratnapura",
    "Trincomalee",
    "Vavuniya",
  ];

  // More sophisticated purple color scheme
  const colors = {
    primary: "#4F46E5", // Indigo
    primaryDark: "#4338CA", // Darker indigo
    secondary: "#7C3AED", // Violet
    accent: "#C4B5FD", // Light violet
    gradientStart: "#8B5CF6", // Violet 500
    gradientEnd: "#6366F1", // Indigo 500
    background: "#F5F3FF", // Very light violet
    cardBg: "#FFFFFF",
    text: "#1F2937", // Gray 800
    textLight: "#6B7280", // Gray 500
    border: "#E0E7FF", // Indigo 100
    error: "#EF4444", // Red 500
    success: "#10B981", // Emerald 500
  };

  return (
    <div
      className="w-full min-h-screen pt-12 pb-16 px-5 sm:px-10 shadow-2xl"
      style={{
        fontFamily: "'Inter', system-ui, sans-serif",
        backgroundColor: "#F9FAFB", // Light gray background
      }}
    >
      <div
        className="mx-auto bg-white p-8 rounded-xl shadow-lg"
        style={{ backgroundColor: colors.cardBg }}
      >
        {/* Header */}
        <div
          className="mb-8 pb-6 border-b"
          style={{ borderColor: colors.border }}
        >
          <h1
            className="text-3xl font-bold mb-2"
            style={{ color: colors.primary }}
          >
            Add New Vehicle
          </h1>
          <p style={{ color: colors.textLight }}>
            Complete the form below to list your vehicle for rental.
          </p>
        </div>

        {/* Progress Bar */}
        <div className="mb-10">
          <div className="flex justify-between mb-2">
            <span
              className="font-medium"
              style={{ color: step >= 1 ? colors.primary : colors.textLight }}
            >
              Vehicle Details
            </span>
            <span
              className="font-medium"
              style={{ color: step >= 2 ? colors.primary : colors.textLight }}
            >
              Images & Description
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2.5 mb-6">
            <div
              className="h-2.5 rounded-full transition-all duration-500 ease-in-out"
              style={{
                width: step === 1 ? "50%" : "100%",
                background: `linear-gradient(to right, ${colors.gradientStart}, ${colors.gradientEnd})`,
              }}
            ></div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div
            className="mb-6 p-4 rounded-lg border-l-4 flex items-start"
            style={{
              backgroundColor: "rgba(239, 68, 68, 0.1)",
              borderLeftColor: colors.error,
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6 mr-3 mt-0.5"
              style={{ color: colors.error }}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
            <span className="text-sm" style={{ color: colors.text }}>
              {error}
            </span>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Step 1: Vehicle Details */}
          <div className={step === 1 ? "block" : "hidden"}>
            <div
              className="bg-white rounded-xl shadow-lg p-8 mb-8"
              style={{
                boxShadow:
                  "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
              }}
            >
              <h2
                className="text-xl font-semibold mb-6"
                style={{ color: colors.primary }}
              >
                Vehicle Information
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
                {/* Vehicle Name */}
                <div className="mb-6">
                  <label
                    className="block text-sm font-medium mb-2"
                    style={{ color: colors.text }}
                  >
                    Vehicle Name <span style={{ color: colors.error }}>*</span>
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full p-3 px-4 border-2 rounded-lg focus:outline-none focus:ring-2 transition-all text-base"
                      style={{
                        borderColor: colors.border,
                        backgroundColor: "white",
                        boxShadow: "0 1px 2px rgba(0,0, 0, 0.05)",
                      }}
                      placeholder="Enter vehicle name"
                    />
                  </div>
                </div>

                {/* Vehicle Type */}
                <div className="mb-6">
                  <label
                    className="block text-sm font-medium mb-2"
                    style={{ color: colors.text }}
                  >
                    Vehicle Type <span style={{ color: colors.error }}>*</span>
                  </label>
                  <div className="relative">
                    <select
                      name="type"
                      value={formData.type}
                      onChange={handleChange}
                      required
                      className="w-full p-3 px-4 border-2 rounded-lg focus:outline-none focus:ring-2 transition-all appearance-none bg-white text-base"
                      style={{
                        borderColor: colors.border,
                        boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
                      }}
                    >
                      <option value="">Select Vehicle Type</option>
                      {vehicleTypes.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4">
                      <svg
                        className="fill-current h-4 w-4"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                      >
                        <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                      </svg>
                    </div>
                  </div>
                </div>

                {/* District */}
                <div className="mb-6">
                  <label
                    className="block text-sm font-medium mb-2"
                    style={{ color: colors.text }}
                  >
                    District <span style={{ color: colors.error }}>*</span>
                  </label>
                  <div className="relative">
                    <select
                      name="district"
                      value={formData.district}
                      onChange={handleChange}
                      required
                      className="w-full p-3 px-4 border-2 rounded-lg focus:outline-none focus:ring-2 transition-all appearance-none bg-white text-base"
                      style={{
                        borderColor: colors.border,
                        boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
                      }}
                    >
                      <option value="">Select District</option>
                      {districts.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4">
                      <svg
                        className="fill-current h-4 w-4"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                      >
                        <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                      </svg>
                    </div>
                  </div>
                </div>

                {/* City */}
                <div className="mb-6">
                  <label
                    className="block text-sm font-medium mb-2"
                    style={{ color: colors.text }}
                  >
                    City <span style={{ color: colors.error }}>*</span>
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      required
                      className="w-full p-3 px-4 border-2 rounded-lg focus:outline-none focus:ring-2 transition-all text-base"
                      style={{
                        borderColor: colors.border,
                        backgroundColor: "white",
                        boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
                      }}
                      placeholder="Enter city"
                    />
                  </div>
                </div>

                {/* Available Seats (all vehicle types except Lorry & Pickup) */}
                {!isLoadVehicle && (
                  <div className="mb-6">
                    <label
                      className="block text-sm font-medium mb-2"
                      style={{ color: colors.text }}
                    >
                      Available Seats{" "}
                      <span style={{ color: colors.error }}>*</span>
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        inputMode="numeric"
                        name="availableSeats"
                        value={formData.availableSeats}
                        onChange={handleNumericChange}
                        required={!isLoadVehicle}
                        className="w-full p-3 px-4 border-2 rounded-lg focus:outline-none focus:ring-2 transition-all text-base"
                        style={{
                          borderColor: colors.border,
                          backgroundColor: "white",
                          boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
                        }}
                        placeholder="Number of seats"
                      />
                    </div>
                  </div>
                )}

                {/* Feature 3: Load capacity (Lorry & Pickup only) */}
                {isLoadVehicle && (
                  <>
                    <div className="mb-6">
                      <label
                        className="block text-sm font-medium mb-2"
                        style={{ color: colors.text }}
                      >
                        Max Load Capacity (kg){" "}
                        <span style={{ color: colors.error }}>*</span>
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          inputMode="numeric"
                          name="maxLoadCapacity"
                          value={formData.maxLoadCapacity}
                          onChange={handleNumericChange}
                          required={isLoadVehicle}
                          className="w-full p-3 px-4 border-2 rounded-lg focus:outline-none focus:ring-2 transition-all text-base"
                          style={{
                            borderColor: colors.border,
                            backgroundColor: "white",
                            boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
                          }}
                          placeholder="e.g. 1500"
                        />
                      </div>
                    </div>

                    <div className="mb-6">
                      <label
                        className="block text-sm font-medium mb-2"
                        style={{ color: colors.text }}
                      >
                        Approximate Cargo Size
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          name="cargoSize"
                          value={formData.cargoSize}
                          onChange={handleChange}
                          className="w-full p-3 px-4 border-2 rounded-lg focus:outline-none focus:ring-2 transition-all text-base"
                          style={{
                            borderColor: colors.border,
                            backgroundColor: "white",
                            boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
                          }}
                          placeholder="e.g. 10ft x 6ft bed"
                        />
                      </div>
                    </div>
                  </>
                )}

                {/* Feature 2: Estimated price per km (optional) */}
                <div className="mb-6">
                  <label
                    className="block text-sm font-medium mb-2"
                    style={{ color: colors.text }}
                  >
                    Price per km (estimated, LKR)
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      inputMode="decimal"
                      name="pricePerKm"
                      value={formData.pricePerKm}
                      onChange={(e) => handleNumericChange(e, { decimal: true })}
                      className="w-full p-3 px-4 border-2 rounded-lg focus:outline-none focus:ring-2 transition-all text-base"
                      style={{
                        borderColor: colors.border,
                        backgroundColor: "white",
                        boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
                      }}
                      placeholder="e.g. 45.50 (optional)"
                    />
                  </div>
                  <p className="text-xs mt-1" style={{ color: colors.textLight }}>
                    Optional. Shown to customers as an estimate — final price may
                    vary by route, traffic and waiting time.
                  </p>
                </div>

                {/* Subscription Plan */}
                <div className="mb-6">
                  <label
                    className="block text-sm font-medium mb-2"
                    style={{ color: colors.text }}
                  >
                    Subscription Plan <span style={{ color: colors.error }}>*</span>
                  </label>
                  <div className="relative">
                    <select
                      name="subscriptionPlan"
                      value={formData.subscriptionPlan}
                      onChange={handleChange}
                      required
                      className="w-full p-3 px-4 border-2 rounded-lg focus:outline-none focus:ring-2 transition-all appearance-none bg-white text-base"
                      style={{
                        borderColor: colors.border,
                        boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
                      }}
                    >
                      <option value="Monthly">Monthly (LKR 750 / Month)</option>
                      <option value="Yearly">Yearly (LKR 8250 / Year)</option>
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4">
                      <svg
                        className="fill-current h-4 w-4"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                      >
                        <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                      </svg>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end">
              <button
                type="button"
                onClick={nextStep}
                className="px-8 py-3 rounded-lg font-medium transition-all"
                style={{
                  background: `linear-gradient(to right, ${colors.gradientStart}, ${colors.gradientEnd})`,
                  color: "white",
                  boxShadow: "0 4px 6px rgba(124, 58, 237, 0.25)",
                }}
              >
                Continue to Images
              </button>
            </div>
          </div>
          {/* Step 2: Images & Description */}
          <div className={step === 2 ? "block" : "hidden"}>
            <div
              className="bg-white rounded-xl shadow-lg p-8 mb-8"
              style={{
                boxShadow:
                  "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
              }}
            >
              <h2
                className="text-xl font-semibold mb-6"
                style={{ color: colors.primary }}
              >
                Vehicle Description
              </h2>

              {/* Description */}
              <div className="mb-6">
                <label
                  className="block text-sm font-medium mb-2"
                  style={{ color: colors.text }}
                >
                  Description <span style={{ color: colors.error }}>*</span>
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  required
                  rows={6}
                  className="w-full p-3 px-4 border-2 rounded-lg focus:outline-none focus:ring-2 transition-all text-base"
                  style={{
                    borderColor: colors.border,
                    backgroundColor: "white",
                    boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
                  }}
                  placeholder="Provide a detailed description of your vehicle including condition, features, and any special notes"
                ></textarea>
              </div>
            </div>

            <div
              className="bg-white rounded-xl shadow-lg p-8 mb-8"
              style={{
                boxShadow:
                  "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
              }}
            >
              <div className="flex justify-between items-center mb-6">
                <h2
                  className="text-xl font-semibold"
                  style={{ color: colors.primary }}
                >
                  Vehicle Images
                </h2>
                <button
                  type="button"
                  onClick={addImageSlot}
                  className="flex items-center py-2 px-4 rounded-lg text-sm font-medium transition-all"
                  style={{
                    background: colors.primary,
                    color: "white",
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5 mr-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 4v16m8-8H4"
                    />
                  </svg>
                  Add Image
                </button>
              </div>

              <p className="text-sm mb-6" style={{ color: colors.textLight }}>
                Upload high-quality images of your vehicle. We recommend
                including exterior, interior, and any special features. At least
                one image is required.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {imageFiles.map((file, index) => (
                  <div key={index} className="relative group">
                    <div
                      className="border-2 rounded-xl p-4 text-center h-56 flex flex-col items-center justify-center overflow-hidden transition-all duration-200"
                      style={{
                        borderColor: file ? colors.primary : colors.border,
                        borderStyle: file ? "solid" : "dashed",
                        backgroundColor: file ? "white" : colors.background,
                      }}
                    >
                      {imagePreviews[index] ? (
                        <div className="relative w-full h-full">
                          <img
                            src={imagePreviews[index]}
                            alt="Preview"
                            className="object-cover w-full h-full rounded-lg"
                          />
                          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 flex items-center justify-center transition-all duration-200 rounded-lg">
                            <button
                              type="button"
                              onClick={() => {
                                const newImageFiles = [...imageFiles];
                                const newImagePreviews = [...imagePreviews];
                                newImageFiles[index] = null;
                                newImagePreviews[index] = null;
                                setImageFiles(newImageFiles);
                                setImagePreviews(newImagePreviews);
                              }}
                              className="bg-red-500 text-white rounded-full w-10 h-10 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-200"
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-5 w-5"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                />
                              </svg>
                            </button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div
                            className="p-4 rounded-full mb-3"
                            style={{ backgroundColor: colors.background }}
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="h-8 w-8"
                              style={{ color: colors.primary }}
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                              />
                            </svg>
                          </div>
                          <p
                            className="text-sm font-medium mb-1"
                            style={{ color: colors.primary }}
                          >
                            Upload Image
                          </p>
                          <p
                            className="text-xs"
                            style={{ color: colors.textLight }}
                          >
                            Click or drag & drop
                          </p>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleImageChange(index, e)}
                            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                            ref={
                              index < 3
                                ? fileInputRefs[index]
                                : additionalFileInputs.current[index - 3]
                            }
                          />
                        </>
                      )}
                    </div>
                    {index >= 3 && (
                      <button
                        type="button"
                        onClick={() => removeImageSlot(index)}
                        className="absolute -top-3 -right-3 bg-red-500 text-white rounded-full w-7 h-7 flex items-center justify-center shadow-md z-20"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="flex justify-between">
              <button
                type="button"
                onClick={prevStep}
                className="px-6 py-3 rounded-lg font-medium border-2 transition-all"
                style={{
                  borderColor: colors.border,
                  color: colors.text,
                }}
              >
                Back to Details
              </button>

              <button
                type="submit"
                disabled={loading}
                className="px-8 py-3 rounded-lg font-medium transition-all flex items-center"
                style={{
                  background: loading
                    ? colors.textLight
                    : `linear-gradient(to right, ${colors.gradientStart}, ${colors.gradientEnd})`,
                  color: "white",
                  boxShadow: loading
                    ? "none"
                    : "0 4px 6px rgba(124, 58, 237, 0.25)",
                  cursor: loading ? "not-allowed" : "pointer",
                  opacity: loading ? 0.7 : 1,
                }}
              >
                {loading && (
                  <svg
                    className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                )}
                {loading ? "Adding Vehicle..." : "Submit Vehicle"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AddVehicle;